import React, { Component } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import lodashGet from 'lodash/get';
import SelectDropdown from '@adsk/bim360-matrix-react-select-dropdown';
import Tooltip from '@adsk/bim360-matrix-react-tooltip';
import IconButton from '@adsk/bim360-matrix-react-icon-button';
import SvgIcon from '@adsk/bim360-matrix-react-svg-icon';
import ActionButton from '@adsk/bim360-matrix-react-action-button';

import './_StatusRippleHeader.scss';

const { Places } = Tooltip;
/**
 * A component for displaying colorful header with title and status change dropdown option, with color change effect.
 * Should be used when displaying an item with status change abilities.
 */
class StatusRippleHeader extends Component {
  /**
   *
   * @param props
   */
  constructor(props) {
    super(props);

    this.state = {
      statusMenuOpen: false,
      /* Used for color transition purposes */
      previousHeaderColorClass: null
    };

    this._transitionEndListener = this._transitionEndListener.bind(this);
    this._handleStatusChange = this._handleStatusChange.bind(this);
  }

  /**
   * _transitionEndListener
   * @private
   */
  _transitionEndListener() {
    this.setState({ animating: false, previousHeaderColorClass: null });
  }

  /**
   * componentDidMount
   */
  componentDidMount() {
    if (this.rippler) {
      this.rippler.addEventListener('transitionend', this._transitionEndListener);
    }
  }

  /**
   * componentWillReceiveProps
   */
  UNSAFE_componentWillReceiveProps(nextProps) {
    if (
      nextProps.changeToStatus &&
      this.props.currentStatusColorClassName !== nextProps.currentStatusColorClassName
    ) {
      this.setState({
        previousHeaderColorClass: this.props.currentStatusColorClassName,
        animating: true
      });
    }

    // If action button got disabled, we need to manually set state to indicate it is not hovered as it won't trigger mouse events
    if (
      !lodashGet(this.props, 'extraActionButton.disabled') &&
      lodashGet(nextProps, 'extraActionButton.disabled')
    ) {
      this.setState({ isActionButtonHover: false });
    }
  }

  /**
   * componentWillUnmount
   */
  componentWillUnmount() {
    if (this.rippler) {
      this.rippler.removeEventListener('transitionend', this._transitionEndListener);
    }
  }

  /**
   * onStatusChange
   * @param newStatus
   */
  _handleStatusChange(newStatus) {
    this.setState({
      previousHeaderColorClass: this.props.currentStatusColorClassName,
      animating: true
    });
    this.props.onStatusChange(newStatus);
    this.setState({ statusMenuOpen: false });
  }

  /**
   * Render the extra action button if got the required prop
   */
  _renderActionButton() {
    const { extraActionButton } = this.props;

    if (!extraActionButton) {
      return null;
    }

    const { icon, svgId, disabled, onClick, tooltipText } = extraActionButton;
    const actionIconButton = (
      <IconButton
        className="StatusRippleHeader__action-button"
        svgId={svgId}
        onClick={onClick}
        disabled={disabled}
      >
        {!svgId && (
          <SvgIcon width="20px" height="20px">
            {icon}
          </SvgIcon>
        )}
      </IconButton>
    );

    if (tooltipText) {
      return (
        <Tooltip
          className="StatusRippleHeader__action-button-tooltip"
          content={tooltipText}
          container={this}
          containerPadding={5}
          place={Places.BOTTOM}
        >
          {actionIconButton}
        </Tooltip>
      );
    }

    return actionIconButton;
  }

  /**
   * Render an actions menu if got the required props
   */
  _renderActionsMenu() {
    const { actionsMenuConfig } = this.props;

    if (!actionsMenuConfig) {
      return null;
    }

    const { options, container, onSelect, tooltipClassName } = actionsMenuConfig;

    return (
      <ActionButton
        className="StatusRippleHeader__actions-menu"
        container={container || this.container}
        options={options}
        onSelect={onSelect}
        tooltipClassName={tooltipClassName}
      />
    );
  }

  /**
   *
   */
  render() {
    const showStatusOptions = this.props.enableStatusChange && this.props.statusOptions.length > 1;
    const statusColorClass = this.props.currentStatusColorClassName;
    const headerClasses = classNames('StatusRippleHeader', {
      [this.state.previousHeaderColorClass]: this.state.animating,
      [statusColorClass]: !this.state.animating
    });
    const ripplerClasses = classNames('StatusRippleHeader__rippler', {
      'StatusRippleHeader__rippler-active': this.state.animating,
      [statusColorClass]: this.state.animating
    });

    return (
      <section
        className={headerClasses}
        ref={node => {
          this.container = node;
        }}
      >
        <div className="StatusRippleHeader__ripple-effect-container">
          <div
            className={ripplerClasses}
            ref={node => {
              this.rippler = node;
            }}
          />
        </div>
        <div className="StatusRippleHeader__title-wrapper">
          <div className="StatusRippleHeader__super-title">{this.props.superTitle}</div>
          <div className="StatusRippleHeader__title">{this.props.title}</div>
        </div>
        <div className="StatusRippleHeader__actions-container">
          <div className="StatusRippleHeader__status">
            {showStatusOptions ? (
              <SelectDropdown
                {...this.props.statusSelectDropdownProps}
                animated
                options={this.props.statusOptions}
                selectedOptions={this.props.currentStatusOption}
                onChange={this._handleStatusChange}
                optionRenderer={this.props.optionRenderer}
              />
            ) : (
              <span className="StatusRippleHeader__current-status">
                {this.props.currentStatusName}
              </span>
            )}
          </div>
          <div className="StatusRippleHeader__action-buttons-wrapper">
            {this._renderActionButton()}
            {this._renderActionsMenu()}
          </div>
        </div>
      </section>
    );
  }
}

StatusRippleHeader.propTypes = {
  /**
   * A callback function for a status menu item selection event
   */
  onStatusChange: PropTypes.func.isRequired,
  /**
   * Function that renders a status option
   */
  optionRenderer: PropTypes.func.isRequired,
  /**
   * Whether the status can be changed
   */
  enableStatusChange: PropTypes.bool.isRequired,
  /**
   * Preview super title. Displayed above title.
   */
  superTitle: PropTypes.string.isRequired,
  /**
   * Accepts string or component to display as title.
   */
  title: PropTypes.node.isRequired,
  /**
   * Available status options
   */
  statusOptions: PropTypes.array,
  /**
   * Selected status option
   */
  currentStatusOption: PropTypes.object,
  /**
   * New status that currently in process of changing to.
   */
  changeToStatus: PropTypes.object,
  /**
   * Current status name
   */
  currentStatusName: PropTypes.string,
  /**
   * Current status color class name
   */
  currentStatusColorClassName: PropTypes.string.isRequired,
  /**
   * Props to pass to status SelectDropdown component
   */
  statusSelectDropdownProps: PropTypes.object,
  /**
   * An optional action button config that will render on the top right (next to the status dropdown) if provided
   */
  extraActionButton: PropTypes.shape({
    /**
     * The svg icon to render as a button.
     * Should be specified if `svgId` is not.
     */
    icon: PropTypes.func,
    /**
     * An svgId that already exists in the app's svg store.
     * Should be specified if `icon` is not.
     */
    svgId: PropTypes.string,
    /**
     * On-click handler function
     */
    onClick: PropTypes.func.isRequired,
    /**
     * A tooltip text to display when hovering the icon button (won't display if not provided)
     */
    tooltipText: PropTypes.string,
    /**
     * Is button disabled flag
     */
    disabled: PropTypes.bool
  }),
  /**
   * An optional actions menu configuration. If provided, the actions menu will be rendered on the top right.
   */
  actionsMenuConfig: PropTypes.shape({
    /**
     * The actions menu options array, should be in a format acceptable by `ActionButton` shared component
     */
    options: PropTypes.array.isRequired,
    /**
     * The container node for the actions menu. If none provided, this component will be set as the
     * actions menu's container
     */
    container: PropTypes.object,
    /**
     * A handler function for the actions menu select event (required only when `actionsMenuOptions` is provided)
     */
    onSelect: PropTypes.func.isRequired,
    /**
     * Class name for the tooltip component of `ActionButton` component
     */
    tooltipClassName: PropTypes.string
  })
};

export default StatusRippleHeader;
